(() => {
  'use strict';

  class ProductModel extends window.theme.DeferredMedia {
    constructor() {
      super();
    }

    loadContent() {
      super.loadContent();

      Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    }

    setupModelViewerUI(errors) {
      if (errors) return;

      this.modelViewerUI = new Shopify.ModelViewerUI(this.querySelector('model-viewer'));
    }
  }

  window.ProductModel = {
    loadShopifyXR() {
      Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: this.setupShopifyXR.bind(this),
        },
      ]);
    },

    setupShopifyXR(errors) {
      if (errors) return;

      if (window.ShopifyXR) {
        document.querySelectorAll('[id^="ModelJSON-"]').forEach((modelJSON) => {
          window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
          modelJSON.remove();
        });

        window.ShopifyXR.setupXRElements();
      } else {
        document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR());
      }
    },
  };

  window.addEventListener('DOMContentLoaded', () => {
    if (window.ProductModel) {
      window.ProductModel.loadShopifyXR();
    }
  });

  const selectors = {
    addToCart: '[data-add-to-cart]',
    addToCartBar: '[data-add-to-cart-bar]',
    addToCartText: '[data-add-to-cart-text]',
    cartBar: '#cart-bar',
    comingSoonText: '[data-coming-soon-text]',
    formWrapper: '[data-form-wrapper]',
    productForm: '[data-product-form]',
    productJson: '[data-product-json]',
    productVariants: '[data-product-variants]',
    popupOpen: '[data-popup-open]',
    submitAdd: '.product__submit__add',
  };

  const classes = {
    isLoading: 'is-loading',
    isVisible: 'is-visible',
  };

  const attributes = {
    addToCartBar: 'data-add-to-cart-bar',
    cartBarEnabled: 'data-cart-bar-enabled',
    cartBarProductNotification: 'data-cart-bar-product-notification',
    cartBarScroll: 'data-cart-bar-scroll',
    comingSoonText: 'data-coming-soon-text',
    stickyEnabled: 'data-sticky-enabled',
  };

  if (!customElements.get('product-component')) {
    customElements.define(
      'product-component',
      class ProductComponent extends HTMLElement {
        constructor() {
          super();

          this.stickyEnabled = this.getAttribute(attributes.stickyEnabled) === 'true';
          this.formWrapper = this.querySelector(selectors.formWrapper);
          this.cartBarEnabled = this.hasAttribute(attributes.cartBarEnabled);
          this.cartBar = this.querySelector(selectors.cartBar);
          this.setCartBarHeight = this.setCartBarHeight.bind(this);
          this.scrollToTop = this.scrollToTop.bind(this);
          this.toggleCartBarOnScroll = this.toggleCartBarOnScroll.bind(this);
          this.setComingSoonText = this.setComingSoonText.bind(this);
          this.setComingSoonTextOnVariantChange = () => requestAnimationFrame(this.setComingSoonText);
          this.unlockTimer = 0;
        }

        connectedCallback() {
          const productJson = this.querySelector(selectors.productJson);
          if ((productJson && !productJson.innerHTML) || !productJson) return;

          const handle = JSON.parse(productJson.innerHTML).handle;
          let productRecord = {};

          if (handle) {
            productRecord = {handle};
          }

          Shopify.Products.recordRecentlyViewed(productRecord);

          if (Shopify.Products && Shopify.Products.recordRecentlyViewed) {
            Shopify.Products.recordRecentlyViewed(productRecord);
          }

          this.form = this.querySelector(selectors.productForm);

          if (this.cartBarEnabled) {
            this.initCartBar();
            this.setCartBarHeight();
            document.addEventListener('theme:scroll', this.toggleCartBarOnScroll);
            document.addEventListener('theme:resize', this.setCartBarHeight);
          }

          this.initComingSoonButtonText();
        }

        initComingSoonButtonText() {
          this.comingSoonTextElement = this.querySelector(selectors.comingSoonText);
          this.comingSoonText = this.comingSoonTextElement?.getAttribute(attributes.comingSoonText);

          if (!this.comingSoonText) return;

          this.setComingSoonText();
          this.addEventListener('theme:variant:change', this.setComingSoonTextOnVariantChange);
        }

        setComingSoonText() {
          if (!this.comingSoonText) return;

          this.querySelectorAll(selectors.addToCartText).forEach((element) => {
            element.textContent = this.comingSoonText;
          });

          this.querySelectorAll(`${selectors.addToCart}, ${selectors.addToCartBar}`).forEach((button) => {
            button.disabled = true;
            button.setAttribute('disabled', 'disabled');
          });
        }

        initCartBar() {
          this.cartBarBtns = this.cartBar.querySelectorAll(selectors.submitAdd);

          if (this.cartBarBtns.length > 0) {
            this.cartBarBtns.forEach((button) => {
              button.addEventListener('click', (event) => {
                event.preventDefault();

                if (event.currentTarget.hasAttribute(attributes.addToCartBar)) {
                  if (this.cartBarEnabled) {
                    event.currentTarget.classList.add(classes.isLoading);
                    event.currentTarget.setAttribute('disabled', 'disabled');
                  }

                  this.form.querySelector(selectors.addToCart).dispatchEvent(new Event('click', {bubbles: true}));
                } else if (event.currentTarget.hasAttribute(attributes.cartBarScroll)) {
                  this.scrollToTop();
                } else if (event.currentTarget.hasAttribute(attributes.cartBarProductNotification)) {
                  this.form.querySelector(selectors.popupOpen)?.dispatchEvent(new Event('click'));
                }
              });

              if (button.hasAttribute(attributes.addToCartBar)) {
                document.addEventListener('theme:product:add-error', this.scrollToTop);
              }
            });
          }

          this.setCartBarHeight();
        }

        scrollToTop() {
          const productVariants = this.querySelector(selectors.productVariants);
          const targetTop = (window.theme.isMobile() ? productVariants || this.form : this).getBoundingClientRect().top;

          window.theme.scrollTo(window.theme.isMobile() ? targetTop - 10 : targetTop);
        }

        toggleCartBarOnScroll() {
          const scrollTop = window.scrollY;
          const formElement = theme.variables.productPageSticky && this.formWrapper ? this.formWrapper : this.form;

          if (formElement && this.cartBar) {
            const showCartBar = scrollTop > formElement.offsetTop + formElement.offsetHeight;
            this.cartBar.classList.toggle(classes.isVisible, showCartBar);
          }
        }

        setCartBarHeight() {
          const cartBarHeight = this.cartBar.offsetHeight;
          document.documentElement.style.setProperty('--cart-bar-height', `${cartBarHeight}px`);
        }

        disconnectedCallback() {
          this.removeEventListener('theme:variant:change', this.setComingSoonTextOnVariantChange);
          document.removeEventListener('theme:product:add-error', this.scrollToTop);

          if (this.cartBarEnabled) {
            document.removeEventListener('theme:scroll', this.toggleCartBarOnScroll);
            document.removeEventListener('theme:resize', this.setCartBarHeight);
          }
        }
      }
    );
  }

  if (!customElements.get('product-model')) {
    customElements.define('product-model', ProductModel);
  }
})();
